/**
 * Pull a human-readable message out of an arbitrary thrown error. Most
 * server responses use the v1 `ApiError` shape (`{message, error,
 * user_message_tr, status_code}`), but a thrown error can also be a plain
 * `Error` (network failure, axios timeout) or even a non-Error value.
 *
 * Returns `undefined` when nothing useful is extractable — callers should
 * fall back to a generic localized message in that case.
 */
export const extractApiErrorMessage = (err: unknown): string | undefined => {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as {
    response?: {
      status?: number;
      data?: {
        message?: unknown;
        error?: unknown;
        user_message_tr?: unknown;
        status?: unknown;
      };
    };
    message?: unknown;
  };
  const data = e.response?.data;
  if (data && typeof data === 'object') {
    // Prefer the user-facing translation, then the generic message, then `error`.
    if (typeof data.user_message_tr === 'string' && data.user_message_tr.trim()) {
      return data.user_message_tr;
    }
    if (typeof data.message === 'string' && data.message.trim()) {
      return data.message;
    }
    if (typeof data.error === 'string' && data.error.trim()) {
      return data.error;
    }
  }
  if (typeof e.message === 'string' && e.message.trim()) return e.message;
  return undefined;
};
